import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { PURPOSES, DIAL_MODES, DATA_SOURCES, AI_TOGGLES } from "@/lib/campaignMeta";
import { SfFilterBuilder } from "@/components/SfFilterBuilder";
import { SfSourcePicker } from "@/components/SfSourcePicker";
import { ScriptEditor } from "@/components/ScriptEditor";
import {
    X, CaretRight, CaretLeft, UploadSimple, CheckCircle, Sparkle, Megaphone,
    Database, Users, Faders, RocketLaunch, Lock,
} from "@phosphor-icons/react";

const STEPS = [
    { n: 1, label: "Campaign Details", icon: Megaphone },
    { n: 2, label: "Data Source", icon: Database },
    { n: 3, label: "Dialing Mode", icon: RocketLaunch },
    { n: 4, label: "Assignment", icon: Users },
    { n: 5, label: "AI Settings", icon: Sparkle },
    { n: 6, label: "Launch", icon: Faders },
];

const DOW = [["mon", "M"], ["tue", "T"], ["wed", "W"], ["thu", "T"], ["fri", "F"], ["sat", "S"], ["sun", "S"]];

function parseCsv(text) {
    const lines = text.split(/\r?\n/).filter((l) => l.trim());
    if (!lines.length) return [];
    const headers = lines[0].split(",").map((h) => h.trim().toLowerCase());
    const idx = (names) => headers.findIndex((h) => names.includes(h));
    const pi = idx(["phone", "phone number", "mobile", "number", "mobilephone"]);
    const ni = idx(["name", "full name", "contact", "customer"]);
    const ei = idx(["email", "email address"]);
    const ci = idx(["company", "account", "organisation", "organization"]);
    const out = [];
    for (let i = 1; i < lines.length; i++) {
        const cols = lines[i].split(",").map((c) => c.trim());
        const phone = pi >= 0 ? cols[pi] : cols[0];
        if (!phone) continue;
        out.push({ name: ni >= 0 ? cols[ni] || "" : "", phone, email: ei >= 0 ? cols[ei] || "" : "", company: ci >= 0 ? cols[ci] || "" : "" });
    }
    return out;
}

export default function CampaignWizard({ onClose, onCreated }) {
    const [step, setStep] = useState(1);
    const [saving, setSaving] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const [sourceCount, setSourceCount] = useState(null);
    const [previewColumns, setPreviewColumns] = useState([]);
    const [sourceSample, setSourceSample] = useState([]);
    const [agents, setAgents] = useState([]);
    const [queues, setQueues] = useState([]);
    const [form, setForm] = useState({
        name: "", description: "", purpose: "sales", dialing_mode: "preview", pacing_ratio: 2,
        data_source: { kind: "csv", filters: {} }, records: [],
        assignment: { queue_id: "", team: "", agent_ids: [] },
        working_hours: { enabled: false, open: "09:00", close: "18:00", days: ["mon", "tue", "wed", "thu", "fri"] },
        retry: { attempts: 2, interval_minutes: 60, max_daily_attempts: 3 },
        recording_enabled: true, leave_voicemail: false,
        ai_settings: { call_summary: true, detect_sentiment: true },
        talking_points: [], objections: [],
    });

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    useEffect(() => {
        api.get("/users").then(({ data }) => setAgents(data.filter((u) => ["agent", "supervisor"].includes(u.role)))).catch(() => {});
        api.get("/settings/queues").then(({ data }) => setQueues(data.queues || [])).catch(() => {});
    }, []);

    const chooseSource = (kind) => {
        set({ data_source: { kind, filters: {} }, records: [] });
        setSourceCount(null);
        setPreviewColumns([]);
        setSourceSample([]);
    };

    const onCsv = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const text = await file.text();
        const recs = parseCsv(text);
        set({ records: recs });
        setSourceCount(recs.length);
        toast.success(`${recs.length} records loaded from ${file.name}`);
    };

    const previewSf = async () => {
        setPreviewing(true);
        try {
            const ds = form.data_source;
            const { data } = await api.post("/campaigns/datasource/preview", {
                kind: ds.kind,
                filters: ds.filters || null,
                list_view_id: ds.list_view_id || null,
                list_view_object: ds.list_view_object || null,
                report_id: ds.report_id || null,
                phone_column: ds.phone_column || null,
                sf_campaign_id: ds.sf_campaign_id || null,
            });
            setSourceCount(data.total);
            setPreviewColumns(data.columns || []);
            setSourceSample(data.sample || []);
            if (data.detected_phone_column && !ds.phone_column) {
                set({ data_source: { ...ds, phone_column: data.detected_phone_column } });
            }
            if (data.note) toast.info(data.note);
            else if (data.total === 0) toast.info("No records with a phone number were found.");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Preview failed");
            setSourceCount(0);
        } finally {
            setPreviewing(false);
        }
    };

    const toggleAgent = (id) => set({ assignment: { ...form.assignment, agent_ids: form.assignment.agent_ids.includes(id) ? form.assignment.agent_ids.filter((x) => x !== id) : [...form.assignment.agent_ids, id] } });
    const toggleDay = (d) => set({ working_hours: { ...form.working_hours, days: form.working_hours.days.includes(d) ? form.working_hours.days.filter((x) => x !== d) : [...form.working_hours.days, d] } });

    const canNext = () => {
        if (step === 1) return form.name.trim().length > 0;
        if (step === 2) return form.data_source.kind === "csv" ? form.records.length > 0 : sourceCount > 0;
        return true;
    };

    const submit = async (launch) => {
        setSaving(true);
        try {
            const payload = { ...form };
            if (form.data_source.kind !== "csv") payload.records = [];
            const { data } = await api.post("/campaigns", payload);
            if (launch) {
                try {
                    await api.post(`/campaigns/${data.id}/status`, { status: "active" });
                    toast.success("Campaign launched");
                } catch {
                    toast.error("Saved as draft, but activation failed — activate it from the list.");
                }
            } else {
                toast.success("Campaign saved as draft");
            }
            onCreated?.();
            onClose();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not create campaign");
        } finally {
            setSaving(false);
        }
    };

    const inputCls = "mt-1.5 w-full rounded-lg border border-stone-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-ink";
    const total = form.data_source.kind === "csv" ? form.records.length : (sourceCount || 0);

    return (
        <div className="fixed inset-0 z-[70] grid place-items-center bg-ink/50 p-3 md:p-6" data-testid="campaign-wizard" onClick={onClose}>
            <div className="flex h-full max-h-[92vh] w-full max-w-5xl overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
                {/* Step rail */}
                <div className="hidden w-60 shrink-0 flex-col border-r border-stone-200 bg-stone-50 p-5 md:flex">
                    <div className="flex items-center gap-2 font-heading text-base font-bold"><Megaphone size={18} weight="fill" className="text-brand" /> New Campaign</div>
                    <div className="mt-6 space-y-1">
                        {STEPS.map((s) => {
                            const active = s.n === step, done = s.n < step;
                            return (
                                <div key={s.n} className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${active ? "bg-ink text-white" : done ? "text-ink" : "text-stone2"}`}>
                                    <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full text-[11px] font-bold ${active ? "bg-white text-ink" : done ? "bg-emerald-500 text-white" : "bg-stone-200 text-stone2"}`}>{done ? <CheckCircle size={14} weight="fill" /> : s.n}</span>
                                    {s.label}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Content */}
                <div className="flex min-w-0 flex-1 flex-col">
                    <div className="flex items-center justify-between border-b border-stone-200 px-6 py-4">
                        <div>
                            <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-stone2">Step {step} of 6</div>
                            <div className="font-heading text-lg font-bold">{STEPS[step - 1].label}</div>
                        </div>
                        <button onClick={onClose} data-testid="wizard-close" className="rounded-full p-1.5 text-stone2 hover:bg-stone-100"><X size={20} /></button>
                    </div>

                    <div className="flex-1 overflow-y-auto px-6 py-5">
                        {/* STEP 1 */}
                        {step === 1 && (
                            <div className="space-y-5">
                                <div>
                                    <label className="text-[11px] font-bold uppercase tracking-widest text-stone2">Campaign Name</label>
                                    <input data-testid="wizard-name" value={form.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. Q3 Renewals Outreach" className={inputCls} />
                                </div>
                                <div>
                                    <label className="text-[11px] font-bold uppercase tracking-widest text-stone2">Description</label>
                                    <textarea data-testid="wizard-description" value={form.description} onChange={(e) => set({ description: e.target.value })} rows={3} placeholder="What is this campaign about?" className={`${inputCls} resize-none`} />
                                </div>
                                <div>
                                    <label className="text-[11px] font-bold uppercase tracking-widest text-stone2">Business Purpose</label>
                                    <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3">
                                        {PURPOSES.map((p) => (
                                            <button key={p.id} type="button" data-testid={`wizard-purpose-${p.id}`} onClick={() => set({ purpose: p.id })} className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition-all ${form.purpose === p.id ? "border-ink bg-ink text-white" : "border-stone-300 bg-white text-ink hover:border-ink"}`}>{p.label}</button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* STEP 2 */}
                        {step === 2 && (
                            <div className="space-y-4">
                                <div className="grid gap-2.5 sm:grid-cols-2">
                                    {DATA_SOURCES.map((s) => {
                                        const active = form.data_source.kind === s.id;
                                        return (
                                            <button key={s.id} type="button" disabled={!s.ready} data-testid={`wizard-source-${s.id}`} onClick={() => s.ready && chooseSource(s.id)} className={`rounded-xl border p-3.5 text-left transition-all ${active ? "border-ink ring-1 ring-ink" : "border-stone-300"} ${s.ready ? "hover:border-ink" : "cursor-not-allowed opacity-55"}`}>
                                                <div className="flex items-center justify-between">
                                                    <span className="text-sm font-bold text-ink">{s.label}</span>
                                                    {!s.ready && <span className="flex items-center gap-1 rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-semibold text-stone2"><Lock size={10} /> Soon</span>}
                                                    {active && <CheckCircle size={16} weight="fill" className="text-ink" />}
                                                </div>
                                                <p className="mt-1 text-xs text-stone2">{s.desc}</p>
                                            </button>
                                        );
                                    })}
                                </div>

                                {form.data_source.kind === "csv" && (
                                    <label data-testid="wizard-csv-drop" className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-stone-300 p-6 text-center hover:border-ink">
                                        <UploadSimple size={24} className="text-stone2" />
                                        <span className="text-sm font-semibold text-ink">Click to upload a CSV</span>
                                        <span className="text-xs text-stone2">Columns: name, phone, email, company</span>
                                        <input type="file" accept=".csv,text/csv" className="hidden" data-testid="wizard-csv-input" onChange={onCsv} />
                                    </label>
                                )}
                                {(form.data_source.kind === "sf_leads" || form.data_source.kind === "sf_contacts") && (
                                    <div className="space-y-3">
                                        <button onClick={previewSf} disabled={previewing} data-testid="wizard-preview-sf" className="w-full rounded-full bg-ink px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50">{previewing ? "Reading Salesforce…" : "Preview from Salesforce"}</button>
                                        <SfFilterBuilder
                                            kind={form.data_source.kind}
                                            filters={form.data_source.filters}
                                            onChange={(filters) => set({ data_source: { ...form.data_source, filters } })}
                                            onEnter={previewSf}
                                        />
                                    </div>
                                )}
                                {(form.data_source.kind === "sf_listview" || form.data_source.kind === "sf_report" || form.data_source.kind === "sf_campaign") && (
                                    <div className="space-y-3">
                                        <SfSourcePicker
                                            kind={form.data_source.kind}
                                            value={form.data_source}
                                            onChange={(patch) => { setSourceCount(null); setPreviewColumns([]); setSourceSample([]); set({ data_source: { ...form.data_source, ...patch } }); }}
                                        />
                                        {form.data_source.kind === "sf_report" && previewColumns.length > 0 && (
                                            <div>
                                                <label className="text-[11px] font-bold uppercase tracking-widest text-stone2">Phone column</label>
                                                <select
                                                    data-testid="report-phone-column"
                                                    value={form.data_source.phone_column || ""}
                                                    onChange={(e) => set({ data_source: { ...form.data_source, phone_column: e.target.value } })}
                                                    className={inputCls}
                                                >
                                                    <option value="">Auto-detect</option>
                                                    {previewColumns.map((c) => <option key={c.api} value={c.api}>{c.label}{c.dataType ? ` (${c.dataType})` : ""}</option>)}
                                                </select>
                                                <p className="mt-1 text-xs text-stone2">Changed the column? Click Preview again.</p>
                                            </div>
                                        )}
                                        <button
                                            onClick={previewSf}
                                            disabled={previewing || (form.data_source.kind === "sf_listview" ? !form.data_source.list_view_id : form.data_source.kind === "sf_campaign" ? !form.data_source.sf_campaign_id : !form.data_source.report_id)}
                                            data-testid="wizard-preview-sf"
                                            className="w-full rounded-full bg-ink px-4 py-2.5 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50"
                                        >{previewing ? "Reading Salesforce…" : "Preview from Salesforce"}</button>
                                    </div>
                                )}

                                {total > 0 && (
                                    <div data-testid="wizard-total-records" className="flex items-center gap-2 rounded-xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700">
                                        <CheckCircle size={18} weight="fill" /> {total} records ready to dial{total >= 500 ? " (showing first 500)" : ""}
                                    </div>
                                )}
                                {sourceSample.length > 0 && (
                                    <div className="overflow-hidden rounded-xl border border-stone-200" data-testid="preview-sample">
                                        <div className="overflow-x-auto">
                                            <table className="w-full text-left text-sm">
                                                <thead className="bg-stone-50 text-[11px] uppercase tracking-wide text-stone2">
                                                    <tr>
                                                        <th className="px-3 py-2 font-semibold">Name</th>
                                                        <th className="px-3 py-2 font-semibold">Phone</th>
                                                        <th className="px-3 py-2 font-semibold">Email</th>
                                                        <th className="px-3 py-2 font-semibold">{["sf_listview", "sf_campaign"].includes(form.data_source.kind) ? "Object" : "Company"}</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {sourceSample.map((r, i) => (
                                                        <tr key={i} className="border-t border-stone-100" data-testid={`preview-sample-row-${i}`}>
                                                            <td className="px-3 py-2 font-medium text-ink">{r.name || "—"}</td>
                                                            <td className="px-3 py-2 text-ink">{r.phone || "—"}</td>
                                                            <td className="px-3 py-2 text-stone2">{r.email || "—"}</td>
                                                            <td className="px-3 py-2 text-stone2">{r.company || r.sf_type || "—"}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                        <div className="border-t border-stone-100 bg-stone-50/60 px-3 py-2 text-xs text-stone2">Sample of {sourceSample.length} — {total} total records will be added.</div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* STEP 3 */}
                        {step === 3 && (
                            <div className="grid gap-2.5 sm:grid-cols-2">
                                {DIAL_MODES.map((m) => {
                                    const active = form.dialing_mode === m.id;
                                    return (
                                        <button key={m.id} type="button" data-testid={`wizard-mode-${m.id}`} onClick={() => set({ dialing_mode: m.id })} className={`rounded-xl border p-4 text-left transition-all ${active ? "border-ink ring-1 ring-ink" : "border-stone-300 hover:border-ink"}`}>
                                            <div className="flex items-center justify-between">
                                                <span className="text-sm font-bold text-ink">{m.label}</span>
                                                {m.ready ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">Ready</span> : <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-semibold text-stone2">Execution coming soon</span>}
                                            </div>
                                            <p className="mt-1.5 text-xs leading-relaxed text-stone2">{m.desc}</p>
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                        {step === 3 && form.dialing_mode === "predictive" && (
                            <div className="mt-4 rounded-xl border border-stone-200 bg-stone-50/60 p-4">
                                <label className="text-[11px] font-bold uppercase tracking-widest text-stone2">Lines per available agent (pacing ratio)</label>
                                <input type="number" step="0.5" min="1" max="5" data-testid="wizard-pacing-ratio"
                                    value={form.pacing_ratio ?? 2}
                                    onChange={(e) => set({ pacing_ratio: Number(e.target.value) })}
                                    className={inputCls} />
                                <p className="mt-1 text-xs text-stone2">e.g. 2 = dial 2 lines for every free agent. Higher = more talk time but more chance of dropped (abandoned) calls.</p>
                            </div>
                        )}

                        {/* STEP 4 */}
                        {step === 4 && (
                            <div className="space-y-5">
                                <div className="grid gap-4 sm:grid-cols-2">
                                    <div>
                                        <label className="text-[11px] font-bold uppercase tracking-widest text-stone2">Queue</label>
                                        <select data-testid="wizard-queue" value={form.assignment.queue_id} onChange={(e) => set({ assignment: { ...form.assignment, queue_id: e.target.value } })} className={inputCls}>
                                            <option value="">— None —</option>
                                            {queues.map((q) => <option key={q.name} value={q.name}>{q.name}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-[11px] font-bold uppercase tracking-widest text-stone2">Team (label)</label>
                                        <input data-testid="wizard-team" value={form.assignment.team} onChange={(e) => set({ assignment: { ...form.assignment, team: e.target.value } })} placeholder="e.g. West Coast Sales" className={inputCls} />
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[11px] font-bold uppercase tracking-widest text-stone2">Individual Agents</label>
                                    <div className="mt-2 flex flex-wrap gap-2" data-testid="wizard-agents">
                                        {agents.length === 0 && <span className="text-sm text-stone2">No agents yet — add them in Settings → Team.</span>}
                                        {agents.map((a) => (
                                            <button key={a.id} type="button" data-testid={`wizard-agent-${a.id}`} onClick={() => toggleAgent(a.id)} className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-all ${form.assignment.agent_ids.includes(a.id) ? "border-ink bg-ink text-white" : "border-stone-300 text-stone2 hover:border-ink"}`}>{a.name}</button>
                                        ))}
                                    </div>
                                </div>
                                <div className="rounded-xl border border-stone-200 p-4">
                                    <div className="flex items-center justify-between">
                                        <span className="text-[11px] font-bold uppercase tracking-widest text-stone2">Working Hours</span>
                                        <Switch data-testid="wizard-wh-toggle" checked={form.working_hours.enabled} onCheckedChange={(v) => set({ working_hours: { ...form.working_hours, enabled: v } })} />
                                    </div>
                                    {form.working_hours.enabled && (
                                        <div className="mt-3 flex flex-wrap items-end gap-3">
                                            <div><label className="text-[10px] text-stone2">Open</label><input type="time" value={form.working_hours.open} onChange={(e) => set({ working_hours: { ...form.working_hours, open: e.target.value } })} className="mt-1 block rounded-lg border border-stone-300 px-2 py-1.5 text-sm" /></div>
                                            <div><label className="text-[10px] text-stone2">Close</label><input type="time" value={form.working_hours.close} onChange={(e) => set({ working_hours: { ...form.working_hours, close: e.target.value } })} className="mt-1 block rounded-lg border border-stone-300 px-2 py-1.5 text-sm" /></div>
                                            <div className="flex gap-1">
                                                {DOW.map(([d, l], i) => <button key={i} type="button" onClick={() => toggleDay(d)} className={`h-8 w-8 rounded-full text-xs font-bold ${form.working_hours.days.includes(d) ? "bg-ink text-white" : "bg-stone-100 text-stone2"}`}>{l}</button>)}
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="grid gap-4 sm:grid-cols-3">
                                    <div><label className="text-[11px] font-bold uppercase tracking-widest text-stone2">Retry Attempts</label><input type="number" min="0" data-testid="wizard-retry-attempts" value={form.retry.attempts} onChange={(e) => set({ retry: { ...form.retry, attempts: +e.target.value } })} className={inputCls} /></div>
                                    <div><label className="text-[11px] font-bold uppercase tracking-widest text-stone2">Retry Interval (min)</label><input type="number" min="0" value={form.retry.interval_minutes} onChange={(e) => set({ retry: { ...form.retry, interval_minutes: +e.target.value } })} className={inputCls} /></div>
                                    <div><label className="text-[11px] font-bold uppercase tracking-widest text-stone2">Max Daily Attempts</label><input type="number" min="0" value={form.retry.max_daily_attempts} onChange={(e) => set({ retry: { ...form.retry, max_daily_attempts: +e.target.value } })} className={inputCls} /></div>
                                </div>
                                <div className="flex flex-col gap-3 sm:flex-row">
                                    <label className="flex flex-1 items-center justify-between rounded-xl border border-stone-200 px-4 py-3 text-sm font-semibold"><span>Call Recording</span><Switch data-testid="wizard-recording" checked={form.recording_enabled} onCheckedChange={(v) => set({ recording_enabled: v })} /></label>
                                    <label className="flex flex-1 items-center justify-between rounded-xl border border-stone-200 px-4 py-3 text-sm font-semibold opacity-70"><span>Leave Voicemail <span className="text-[10px] font-normal text-stone2">(coming soon)</span></span><Switch data-testid="wizard-voicemail" checked={form.leave_voicemail} onCheckedChange={(v) => set({ leave_voicemail: v })} /></label>
                                </div>
                            </div>
                        )}

                        {/* STEP 5 */}
                        {step === 5 && (
                            <div className="space-y-5">
                                <div className="space-y-2.5">
                                    {AI_TOGGLES.map((t) => (
                                        <label key={t.id} className="flex items-center justify-between rounded-xl border border-stone-200 px-4 py-3">
                                            <div>
                                                <div className="text-sm font-semibold text-ink">{t.label}</div>
                                                <div className="text-xs text-stone2">{t.desc}</div>
                                            </div>
                                            <Switch data-testid={`wizard-ai-${t.id}`} checked={!!form.ai_settings[t.id]} onCheckedChange={(v) => set({ ai_settings: { ...form.ai_settings, [t.id]: v } })} />
                                        </label>
                                    ))}
                                </div>
                                <div className="border-t border-stone-200 pt-5">
                                    <div className="mb-1 font-heading text-sm font-bold text-ink">Agent Script</div>
                                    <ScriptEditor
                                        talkingPoints={form.talking_points}
                                        objections={form.objections}
                                        onChange={({ talking_points, objections }) => set({ talking_points, objections })}
                                        purpose={form.purpose}
                                        name={form.name}
                                        description={form.description}
                                    />
                                </div>
                            </div>
                        )}

                        {/* STEP 6 */}
                        {step === 6 && (
                            <div className="space-y-3" data-testid="wizard-review">
                                {[
                                    ["Name", form.name || "—"],
                                    ["Purpose", PURPOSES.find((p) => p.id === form.purpose)?.label],
                                    ["Data source", DATA_SOURCES.find((s) => s.id === form.data_source.kind)?.label],
                                    ["Total records", total],
                                    ["Dialing mode", DIAL_MODES.find((m) => m.id === form.dialing_mode)?.label],
                                    ["Agents", form.assignment.agent_ids.length || "—"],
                                    ["Recording", form.recording_enabled ? "On" : "Off"],
                                    ["AI features", AI_TOGGLES.filter((t) => form.ai_settings[t.id]).map((t) => t.label).join(", ") || "None"],
                                ].map(([k, v]) => (
                                    <div key={k} className="flex items-center justify-between border-b border-stone-100 pb-2 text-sm">
                                        <span className="text-stone2">{k}</span>
                                        <span className="font-semibold text-ink">{v}</span>
                                    </div>
                                ))}
                                <p className="pt-2 text-xs text-stone2">Launching activates the campaign. You can pause it any time from the campaign list.</p>
                            </div>
                        )}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between border-t border-stone-200 px-6 py-4">
                        <button onClick={() => (step > 1 ? setStep(step - 1) : onClose())} data-testid="wizard-back" className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold text-stone2 hover:bg-stone-100">
                            <CaretLeft size={15} /> {step > 1 ? "Back" : "Cancel"}
                        </button>
                        {step < 6 ? (
                            <button onClick={() => canNext() ? setStep(step + 1) : toast.error(step === 2 ? "Load records to continue" : "Please fill required fields")} data-testid="wizard-next" className="inline-flex items-center gap-1.5 rounded-full bg-ink px-6 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5">
                                Continue <CaretRight size={15} />
                            </button>
                        ) : (
                            <div className="flex gap-2">
                                <button onClick={() => submit(false)} disabled={saving} data-testid="wizard-save-draft" className="rounded-full border border-stone-300 px-5 py-2.5 text-sm font-semibold text-ink hover:border-ink disabled:opacity-50">Save as Draft</button>
                                <button onClick={() => submit(true)} disabled={saving} data-testid="wizard-launch" className="inline-flex items-center gap-1.5 rounded-full bg-brand px-6 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 disabled:opacity-50">
                                    <RocketLaunch size={15} weight="fill" /> {saving ? "Launching…" : "Launch Campaign"}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
